import Box from '@mui/material/Box';
import { ColoredChip, ColoredIcon, TIconName, useEntityColor } from '../ColoredIconAndChip';
import { IdentityStatus } from './types';
import { EntityStatus } from '@verifime/utils';

export interface IdentityStatusChipProp {
  status?: IdentityStatus;
  isCompact?: boolean;
}

type TIconMapping = {
  iconName: TIconName;
  entityStatus: EntityStatus;
};

const ICON_MAPPING: { [key in IdentityStatus]: TIconMapping } = {
  [IdentityStatus.DRAFT]: {
    iconName: 'editRounded',
    entityStatus: EntityStatus.Draft,
  },
  [IdentityStatus.IN_PROGRESS]: {
    iconName: 'pending',
    entityStatus: EntityStatus.InProgress,
  },
  [IdentityStatus.REJECTED]: {
    iconName: 'errorRounded',
    entityStatus: EntityStatus.Rejected,
  },
  [IdentityStatus.VERIFIED]: {
    iconName: 'verifiedRounded',
    entityStatus: EntityStatus.Verified,
  },
};

export default function IdentityStatusChip({ status, isCompact }: IdentityStatusChipProp) {
  // useEntityColor must run on every render regardless of status, so it is called before the
  // status guard below rather than after it. When status is absent there is nothing to map, so it
  // falls back to EntityStatus.Empty; the resulting colour is never read in that case, because the
  // guard below still returns before anything uses it.
  const iconMapping = status !== undefined ? ICON_MAPPING[status] : undefined;
  const color = useEntityColor(iconMapping?.entityStatus ?? EntityStatus.Empty);

  if (status === undefined || iconMapping === undefined) {
    return;
  }

  if (isCompact) {
    return (
      <ColoredIcon iconName={iconMapping.iconName} isCompact={isCompact} statusColor={color} />
    );
  }

  return (
    <Box>
      <ColoredChip
        icon={<ColoredIcon iconName={iconMapping.iconName} />}
        statusColor={color}
        label={status}
      />
    </Box>
  );
}
